import type { ClientApiPageData, Experiment, PageConfig } from "../types.ts";
import { WIDGET_DEFAULTS } from "./constants.ts";

/**
 * Gets page configuration data from window.clientApiData
 * Falls back to default configuration if clientApiData is not available
 */
export async function getPageConfig(): Promise<PageConfig> {
  if (window.clientApiData == undefined) {
    return getPageDataLegacy();
  }

  const pageData = window.clientApiData?.pageData;
  const paletteData = window.clientApiData?.paletteData;

  if (!pageData) {
    throw new Error("Page data not available");
  }

  return {
    id: pageData.resourceID || "",
    tags: pageData.contentTags || [],
    property: pageData.property || "",
    siteHost: getArticleUrl().hostname,
    autoTags: pageData.autoTags || [],
    manualTags: pageData.contentTags || [],
    theme: {
      primaryColor:
        paletteData?.colors?.primary ||
        WIDGET_DEFAULTS.FALLBACK_THEME.primaryColor,
      blueColor:
        paletteData?.colors?.blue || WIDGET_DEFAULTS.FALLBACK_THEME.blueColor,
      fontNormal: {
        fontFamily: paletteData.typography.normal.fontFamily,
        fontWeight: paletteData.typography.normal.fontWeight,
        sizeSmall: paletteData.typography.normal.screenSizes.small,
        sizeMedium: paletteData.typography.normal.screenSizes.medium,
        sizeLarge: paletteData.typography.normal.screenSizes.large,
      },
      fontLarge: {
        fontFamily: paletteData.typography.large.fontFamily,
        fontWeight: paletteData.typography.large.fontWeight,
        sizeSmall: paletteData.typography.large.screenSizes.small,
        sizeMedium: paletteData.typography.large.screenSizes.medium,
        sizeLarge: paletteData.typography.large.screenSizes.large,
      },
    },
    siteRenderer: "lightning",
  };
}

/**
 * Gets page configuration data from legacy mmClientApi used by SRS
 * Falls back to default configuration if mmClientApi is not available
 */
async function getPageDataLegacy(): Promise<PageConfig> {
  const getPageData = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("Page data timeout"));
      }, 10000);

      window.mmClientApi.push("getPageData", (data) => {
        clearTimeout(timeout);
        resolve(data);
      });
    });
  };

  const getPaletteData = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("Palette data timeout"));
      }, 10000);

      window.mmClientApi.push("getPaletteData", (data) => {
        clearTimeout(timeout);
        resolve(data);
      });
    });
  };

  const [pageData, paletteData] = await Promise.all([
    getPageData(),
    getPaletteData(),
  ]);

  return {
    id: pageData.resourceID || "",
    tags: pageData.contentTags || [],
    property: pageData.property || "",
    siteHost: getArticleUrl().hostname,
    autoTags: pageData.autoTags || [],
    manualTags: pageData.contentTags || [],
    theme: {
      primaryColor:
        paletteData.colors?.primary ||
        WIDGET_DEFAULTS.FALLBACK_THEME.primaryColor,
      blueColor:
        paletteData.colors?.blue || WIDGET_DEFAULTS.FALLBACK_THEME.blueColor,
      fontNormal: {
        fontFamily: paletteData.typography.normal.fontFamily,
        fontWeight: paletteData.typography.normal.fontWeight,
        sizeSmall: paletteData.typography.normal.screenSizes.small,
        sizeMedium: paletteData.typography.normal.screenSizes.medium,
        sizeLarge: paletteData.typography.normal.screenSizes.large,
      },
      fontLarge: {
        fontFamily: paletteData.typography.large.fontFamily,
        fontWeight: paletteData.typography.large.fontWeight,
        sizeSmall: paletteData.typography.large.screenSizes.small,
        sizeMedium: paletteData.typography.large.screenSizes.medium,
        sizeLarge: paletteData.typography.large.screenSizes.large,
      },
    },
    siteRenderer: "srs",
  };
}

/**
 * Gets the BlueConic user ID from the client-side API with improved error handling
 * @returns Promise<string | null> The BlueConic user ID or null if not available
 */
export const getBlueConicUserID = async (): Promise<string | null> => {
  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      console.warn("[MM Recirculation Widget] BlueConic loading timeout");
      resolve(null);
    }, 5000); // 5 second timeout

    const getBcUserId = () => {
      clearTimeout(timeoutId);
      try {
        if (
          typeof window.blueConicClient !== "undefined" &&
          typeof window.blueConicClient.profile !== "undefined"
        ) {
          const profile = window.blueConicClient.profile.getProfile();
          const userId = profile.getId();
          resolve(userId || null);
        } else {
          resolve(null);
        }
      } catch (error) {
        console.error(
          "[MM Recirculation Widget] Error getting BlueConic user ID:",
          error
        );
        resolve(null);
      }
    };

    // Check if BlueConic is already loaded
    if (
      typeof window.blueConicClient !== "undefined" &&
      typeof window.blueConicClient.profile !== "undefined"
    ) {
      getBcUserId();
    } else {
      // Wait for BlueConic to load - the event will fire when ready
      window.addEventListener("onBlueConicLoaded", getBcUserId, { once: true });
    }
  });
};

/**
 * Gets the current article URL from canonical link or window.location
 */
function getArticleUrl(): URL {
  try {
    const canonicalLinkElement = document.querySelector(
      "link[rel='canonical']"
    );

    if (canonicalLinkElement) {
      const href = (canonicalLinkElement as HTMLLinkElement)?.href;
      if (href) {
        return new URL(href);
      }
    }

    return new URL(window.location.href);
  } catch (error) {
    console.error(
      "[MM Recirculation Widget] Error parsing article URL:",
      error
    );
    return new URL(window.location.href);
  }
}
